import type {
  ContactabilityInput,
  LeadScoreConfig,
  LeadScoreEngineResult,
  SignalContext,
} from './types';

export const computeLeadScore = (
  config: LeadScoreConfig,
  ctx: SignalContext,
  contactability: ContactabilityInput
): LeadScoreEngineResult => {
  const signals: LeadScoreEngineResult['signals'] = [];
  let pressureFired = 0;
  let contextFired = 0;

  for (const [signalId, definition] of Object.entries(config.signals)) {
    const outcome = definition.evaluate(ctx);

    if (outcome.status === 'fired') {
      if (definition.category === 'pressure') {
        pressureFired += 1;
      } else {
        contextFired += 1;
      }
    }

    signals.push({
      id: signalId,
      label: definition.label,
      category: definition.category,
      status: outcome.status,
      detail: outcome.status === 'unavailable' ? outcome.reason : undefined,
      evidence: outcome.status === 'unavailable' ? undefined : outcome.evidence,
    });
  }

  const count = pressureFired + contextFired;
  let base =
    config.baseThresholds.find((threshold) => count >= threshold.count)?.base ??
    0;

  const { pressureGate } = config;
  if (
    base >= pressureGate.minBase &&
    pressureGate.requiresPressure &&
    pressureFired === 0
  ) {
    base = pressureGate.gatedBase;
  }

  const mod = config.contactabilityModifier(contactability);
  const score = Math.round(base * mod);

  const tier =
    config.tierThresholds.find((threshold) => score >= threshold.score)?.tier ??
    'low';
  const action = config.actionByTier[tier];
  const reasons = signals
    .filter((signal) => signal.status === 'fired')
    .map((signal) => config.reasonText[signal.id])
    .filter(Boolean);
  const { headline, description } = config.copyByTier[tier];

  return {
    score,
    tier,
    action,
    signals,
    reasons,
    headline,
    description,
  };
};
